import type { TabItem } from '#/components/ui/Tabs';
import { UnreachableCaseError } from '#/lib/error';

import type { ChartTimeframeId } from './types';

export interface ChartDateRangeParams {
  start_date: string;
  end_date: string;
}

export interface ChartQueryPolicy {
  staleTime: number;
  gcTime: number;
}

export interface ChartTimeframeBounds {
  startDate: Date;
  endDate: Date;
}

const NEW_YORK_TIME_ZONE = 'America/New_York';

export const CHART_TIMEFRAME_TABS_MAP: Record<
  ChartTimeframeId,
  { label: string; group?: string }
> = {
  '10Y': { label: '10 Years' },
  '5Y': { label: '5 Years' },
  '1Y': { label: '1 Year' },
  '6M': { label: '6 Months', group: 'middle' },
  '1M': { label: '1 Month' },
  '1W': { label: '1 Week' },
  INTRADAY: { label: '1 Day' },
  YTD: { label: 'YTD', group: 'end' },
  ALL: { label: 'All Time' },
};

export const CHART_TIMEFRAME_TABS: TabItem<ChartTimeframeId>[] = Object.entries(
  CHART_TIMEFRAME_TABS_MAP,
).map(([id, { label, group }]) => ({
  id: id as ChartTimeframeId,
  label,
  group,
}));

export const MUTUAL_FUND_CHART_TIMEFRAME_TABS: TabItem<ChartTimeframeId>[] = [
  { id: '1Y', label: '1 Year' },
  { id: '6M', label: '6 Months', group: 'middle' },
  { id: '1M', label: '1 Month' },
  { id: '1W', label: '1 Week' },
  { id: 'YTD', label: 'YTD', group: 'end' },
];

function getDatePartsInTimeZone(baseDate: Date): {
  year: number;
  month: number;
  day: number;
} {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: NEW_YORK_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(baseDate);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);

  return { year, month, day };
}

function getNewYorkCalendarDate(baseDate: Date): Date {
  const { year, month, day } = getDatePartsInTimeZone(baseDate);
  return new Date(Date.UTC(year, month - 1, day));
}

function formatApiDate(date: Date): string {
  const day = `${date.getUTCDate()}`.padStart(2, '0');
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const year = `${date.getUTCFullYear()}`;

  return `${day}-${month}-${year}`;
}

export function getChartTimeframeParams(
  timeframe: ChartTimeframeId,
  baseDate: Date = new Date(),
): ChartDateRangeParams {
  const { startDate, endDate } = getChartTimeframeBounds(timeframe, baseDate);

  return {
    start_date: formatApiDate(startDate),
    end_date: formatApiDate(endDate),
  };
}

export function getChartTimeframeBounds(
  timeframe: ChartTimeframeId,
  baseDate: Date = new Date(),
): ChartTimeframeBounds {
  const endDate = getNewYorkCalendarDate(baseDate);
  const startDate = new Date(endDate);

  switch (timeframe) {
    case '10Y':
      startDate.setUTCFullYear(startDate.getUTCFullYear() - 10);
      break;
    case '5Y':
      startDate.setUTCFullYear(startDate.getUTCFullYear() - 5);
      break;
    case '1Y':
      startDate.setUTCFullYear(startDate.getUTCFullYear() - 1);
      break;
    case '6M':
      startDate.setUTCMonth(startDate.getUTCMonth() - 6);
      break;
    case '1M':
      startDate.setUTCMonth(startDate.getUTCMonth() - 1);
      break;
    case '1W':
      startDate.setUTCDate(startDate.getUTCDate() - 7);
      break;
    case 'INTRADAY':
      break;
    case 'YTD':
      startDate.setUTCMonth(0, 1);
      break;
    case 'ALL':
      startDate.setUTCFullYear(1970, 0, 1);
      break;
    default:
      throw new UnreachableCaseError(timeframe);
  }

  return {
    startDate,
    endDate,
  };
}

export function getChartQueryPolicy(
  timeframe: ChartTimeframeId,
): ChartQueryPolicy {
  switch (timeframe) {
    case 'INTRADAY':
    case '1W':
    case '1M':
      return {
        staleTime: 15 * 60 * 1000,
        gcTime: 4 * 60 * 60 * 1000,
      };
    case '6M':
    case '1Y':
    case 'YTD':
      return {
        staleTime: 60 * 60 * 1000,
        gcTime: 12 * 60 * 60 * 1000,
      };
    case '5Y':
    case '10Y':
    case 'ALL':
      return {
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 7 * 24 * 60 * 60 * 1000,
      };
    default:
      throw new UnreachableCaseError(timeframe);
  }
}

export { NEW_YORK_TIME_ZONE };
